import { useMutation, useQuery } from "@tanstack/react-query";
import { Module } from "../../../assets/types/common";
import { Constants } from "../../../assets/utils/lib/constants";
import { SearchDto } from "../../../assets/utils/lib/searchDto";
import { IActionResponse, IActionResult, LookupGroupClient, LookupTypeClient, LookupValueClient, } from "../../../services/proxies";
import { ViewProps } from "../props";

export class LookupValueService<T extends ViewProps.ValueTypeDto> {
    private lookupValueClient: LookupValueClient<T>;
    private lookupTypeClient: LookupTypeClient<ViewProps.CategoryTypeDto>;
    private lookupGroupClient: LookupGroupClient<ViewProps.GroupTypeDto>;

    public constructor(moduleType: Module) {
        this.lookupValueClient = new LookupValueClient<T>(moduleType);
        this.lookupTypeClient = new LookupTypeClient<ViewProps.CategoryTypeDto>(moduleType);
        this.lookupGroupClient = new LookupGroupClient<ViewProps.GroupTypeDto>(moduleType);
    }

    save = () =>
        useMutation<IActionResult<T>, Error, T>({
            mutationFn: async (payload: T) => {
                return await this.lookupValueClient.saveLookupValueAsync(payload);
            },
        });

    getLookupValueQuery = (id: string | undefined) =>
        useQuery({
            queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.Get], id],
            queryFn: async () => {
                return await this.lookupValueClient.getLookupValueAsync(id)
            },
            enabled: Boolean(id) && id !== Constants.emptyGuid,
        });

    getLookupValuesQuery = (valueId: string | undefined) =>
        useQuery({
            queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.GetAll], valueId],
            queryFn:
                async () => await this.lookupValueClient.getLookupValuesAsync(valueId),
            enabled: Boolean(valueId),
        });

    getLookupTypesAsLookupQuery = () =>
        useQuery({
            queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.GetLookupTypes_Lookup]],
            queryFn: async () => await this.lookupTypeClient.getLookupTypesAsLookupAsync(),
        });


    getLookupGroupAsLookupQuery = (typeId: string | undefined) =>
        useQuery({
            queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.GetLookupGroups_Lookup], typeId],
            queryFn: async () => {
                if (typeId) {
                    return await this.lookupGroupClient.getLookupGroupsAsLookupAsync(typeId);
                }
                else
                    return undefined;
            },
            enabled: typeId !== undefined,
        });

    gridQuery = (searchQuery: SearchDto) =>
        useQuery({
            queryKey: [ViewProps.LookupValueQueryKeys[ViewProps.LookupValueQueryTypes.Grid], searchQuery],
            queryFn: async () =>
                await this.lookupValueClient.searchLookupValuesAsync(searchQuery),
        });

    deActivate = () =>
        useMutation<IActionResponse, Error, string>({
            mutationFn: async (id: string | undefined) => {
                return await this.lookupValueClient.deleteLookupValueAsync(id);
            },
        });
}